import { withSentry } from "@sentry/remix";
import "@openfonts/source-sans-pro_all/index.css";
import type { LinksFunction, MetaFunction } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
  useLocation,
} from "@remix-run/react";
import React, { useEffect, useRef } from "react";
import stylesheet from "~/tailwind.css?url";
import { RootLoaderContext, WindowWithENV } from "./types/global";
import InternalServerError from "./components/InternalServerError";
import { useHydrated } from "./hooks/useHydrated";
import honeybadger from "~/services/honeybadger/index.client";

export const meta: MetaFunction = ({ error }) => {
  const title = error ? "Page Not Found | ShirtSpace" : "";

  return [{ title }];
};

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  {
    rel: "preconnect",
    href: "https://use.typekit.net",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    as: "style",
    href: "https://use.typekit.net/ysb3beq.css",
  },
  {
    href: "https://use.typekit.net/ysb3beq.css",
    rel: "stylesheet",
  },
];

export const shouldRevalidate: ShouldRevalidateFunction = () => false;

export const loader = async () => {
  const { ENV } = {
    ENV: {
      ENVIRONMENT: process.env.NODE_ENV,
      FRONTCHAT_ID: process.env.FRONTCHAT_ID,
      GOOGLE_TAG_MANAGER_ID: process.env.GOOGLE_TAG_MANAGER_ID,
      HONEYBADGER_API_KEY: process.env.HONEYBADGER_API_KEY,
      TRUSTPILOT_BUSINESS_UNIT_ID: process.env.TRUSTPILOT_BUSINESS_UNIT_ID,
      URL: process.env.URL,
      EASY_SIZE_APP_ID: process.env.EASY_SIZE_APP_ID,
    },
  } as WindowWithENV;

  return {
    ENV,
  };
};

const App: React.FC = () => {
  const head = useRef<HTMLElement>(null);
  const { state } = useLocation();
  const { ENV } = useLoaderData<typeof loader>();
  const isHydrated = useHydrated();

  useEffect(() => {
    if (!isHydrated || !ENV.HONEYBADGER_API_KEY) return;

    honeybadger.configure({
      apiKey: ENV.HONEYBADGER_API_KEY,
      environment: ENV.ENVIRONMENT ?? "development",
    });
  }, [isHydrated]);

  return (
    <html
      lang="en"
      className="h-full"
    >
      <head ref={head}>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1"
        />
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)}`,
          }}
        />
      </head>
      <body className={`${state?.isMobileMenuOpen ? "overflow-hidden" : ""}`}>
        <Outlet context={{ head } satisfies RootLoaderContext} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
};

export const ErrorBoundary = () => {
  return (
    <html>
      <head>
        <meta
          name="viewport"
          content="width=device-width"
        />
        <Meta />
        <Links />
      </head>
      <body>
        <InternalServerError />
      </body>
    </html>
  );
};

export default withSentry(App);
